import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SortableListItem {
  id: string;
}

export interface SortableListProps<T extends SortableListItem> {
  /** Ordered array of items to render. Must be controlled — pass `onReorder` result back as new `items`. */
  items: T[];
  /** Called with the fully reordered array after a successful drop. */
  onReorder: (reordered: T[]) => void;
  /** Render the content area of each row. Receives the item and its current index. */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** When `true`, hides drag handles and disables all drag interactions. */
  disabled?: boolean;
  /** Extra classes applied to the list container. */
  className?: string;
}

// ─── SortableItem (private) ───────────────────────────────────────────────────

interface SortableItemProps<T extends SortableListItem> {
  item: T;
  index: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  disabled: boolean;
}

function SortableItem<T extends SortableListItem>({
  item,
  index,
  renderItem,
  disabled,
}: SortableItemProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        // Layout
        'flex items-center gap-2',
        // Surface
        'rounded-[var(--radius)] border border-border bg-background',
        // Spacing
        'px-3 py-2',
        // Drag-active elevation
        isDragging ? 'shadow-lg z-10 relative opacity-95' : 'shadow-none',
      )}
    >
      {/* ── Drag handle ───────────────────────────────────────────────── */}
      {!disabled && (
        <button
          ref={setActivatorNodeRef}
          type="button"
          className={cn(
            'flex shrink-0 items-center justify-center',
            'rounded-sm text-muted-foreground',
            'cursor-grab active:cursor-grabbing',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            'transition-colors hover:text-foreground',
            // Ensure a comfortable click target
            'h-6 w-6',
          )}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden="true" />
        </button>
      )}

      {/* ── Item content ──────────────────────────────────────────────── */}
      <div className="min-w-0 flex-1">{renderItem(item, index)}</div>
    </li>
  );
}

// ─── SortableList ─────────────────────────────────────────────────────────────

/**
 * Drag-and-drop sortable list with keyboard accessibility (WCAG 2.1 AA).
 *
 * Built on `@dnd-kit/core` + `@dnd-kit/sortable`. The list is fully controlled —
 * you receive the reordered array via `onReorder` and must pass it back as `items`.
 *
 * **Keyboard**: Tab to the grip handle → Space to pick up → Arrow Up/Down to move
 * → Space or Enter to drop → Escape to cancel.
 *
 * @example
 * ```tsx
 * const [items, setItems] = React.useState([
 *   { id: '1', label: 'First' },
 *   { id: '2', label: 'Second' },
 * ]);
 *
 * <SortableList
 *   items={items}
 *   onReorder={setItems}
 *   renderItem={(item) => <span>{item.label}</span>}
 * />
 * ```
 */
function SortableList<T extends SortableListItem>({
  items,
  onReorder,
  renderItem,
  disabled = false,
  className,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require a small movement before activating drag to preserve click events
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      onReorder(arrayMove(items, oldIndex, newIndex));
    },
    [items, onReorder],
  );

  const itemIds = React.useMemo(() => items.map((item) => item.id), [items]);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <ul aria-label="Sortable list" className={cn('flex flex-col gap-2', className)}>
          {items.map((item, index) => (
            <SortableItem
              key={item.id}
              item={item}
              index={index}
              renderItem={renderItem}
              disabled={disabled}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

SortableList.displayName = 'SortableList';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { SortableList };
